import {
  DeleteOutlined,
  DownOutlined,
  HolderOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Switch,
  Tag,
  Upload,
} from "antd";
import {Reorder, useDragControls} from "framer-motion";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  CustomLinkBlock,
  EmailSignupBlock,
  FaqBlock,
  OfferingCollectionBlock,
  OfferingKind,
  OfferingReference,
  PublicProfileBlock,
  RichTextBlock,
  TestimonialsBlock,
} from "../domain";
import {OfferingCategory, useGetOfferings} from "../../react-query-hooks/offerings";
import {useUploadPublicProfileImage} from "../hooks";
import {BlockCard, FieldGroup, RepeaterItem} from "./public-profile-page.style";

const OFFERING_KIND_BY_CATEGORY: Record<OfferingCategory, OfferingKind> = {
  services: "service",
  digitalProducts: "digital-product",
  productServices: "product-service",
  liveEvents: "live-event",
  subscriptions: "subscription",
};

const TEXT_BLOCK_EDITOR_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{align: ""}, {align: "center"}, {align: "right"}],
    ["link"],
    [{list: "ordered"}, {list: "bullet"}],
  ],
};

const TEXT_BLOCK_EDITOR_FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "align",
  "link",
  "list",
  "bullet",
];

function offeringReferenceValue(reference: OfferingReference): string {
  return `${reference.kind}:${reference.id}`;
}

function parseOfferingReference(value: string): OfferingReference | null {
  const separator = value.lastIndexOf(":");
  const kind = value.slice(0, separator) as OfferingKind;
  const id = Number(value.slice(separator + 1));
  if (
    !Object.values(OFFERING_KIND_BY_CATEGORY).includes(kind) ||
    !Number.isInteger(id) ||
    id < 1
  ) {
    return null;
  }
  return {kind, id};
}

export function getPublicProfileBlockName(block: PublicProfileBlock): string {
  if ("title" in block && typeof block.title === "string" && block.title.trim()) {
    return block.title;
  }
  const names: Record<PublicProfileBlock["type"], string> = {
    offeringCollection: "Offerings",
    customLink: "Custom links",
    socialLinks: "Social links",
    richText: "Texts",
    image: "Image",
    divider: "Divider",
    video: "Video",
    testimonials: "Testimonials",
    countdown: "Countdown",
    embed: "Embed",
    emailSignup: "Email signup",
    faq: "Frequently asked questions",
  };
  return names[block.type];
}

function optionalString(value: string): string | null {
  return value.trim() || null;
}

function replaceAt<T>(items: T[], index: number, value: T): T[] {
  return items.map((item, itemIndex) => (itemIndex === index ? value : item));
}

function removeAt<T>(items: T[], index: number): T[] {
  return items.filter((_, itemIndex) => itemIndex !== index);
}

function RepeaterHeader({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="repeater-header">
      <strong>{label}</strong>
      <Button
        type="text"
        danger
        size="small"
        icon={<DeleteOutlined />}
        aria-label={`Remove ${label}`}
        disabled={disabled}
        onClick={onRemove}
      />
    </div>
  );
}

function SelectedOfferingPicker({
  offerings,
  onChange,
  category,
  categories,
  label = "Selected offerings",
}: {
  offerings: OfferingReference[];
  onChange: (offerings: OfferingReference[]) => void;
  category?: OfferingCategory;
  categories?: OfferingCategory[];
  label?: string;
}) {
  const offeringsQuery = useGetOfferings();
  const availableOptions = (offeringsQuery.data ?? [])
    .filter(
      (offering) =>
        (!category || offering.category === category) &&
        (!categories || categories.includes(offering.category))
    )
    .map((offering) => ({
      value: offeringReferenceValue({
        kind: OFFERING_KIND_BY_CATEGORY[offering.category],
        id: offering.offeringId,
      }),
      label: offering.offeringName,
    }));
  const availableValues = new Set(availableOptions.map((option) => option.value));
  const unavailableOptions = offerings
    .map((offering) => offeringReferenceValue(offering))
    .filter((value) => !availableValues.has(value))
    .map((value) => ({value, label: `${value} (currently unavailable)`}));

  return (
    <FieldGroup>
      <label>{label}</label>
      <Select
        mode="multiple"
        showSearch={!category}
        showArrow
        suffixIcon={<DownOutlined />}
        maxTagCount="responsive"
        value={offerings.map(offeringReferenceValue)}
        options={[...availableOptions, ...unavailableOptions]}
        loading={offeringsQuery.isLoading}
        placeholder={category === "services" ? "Select sessions" : "Choose offerings"}
        style={{width: "100%"}}
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        onChange={(values: string[]) =>
          onChange(
            values
              .slice(0, 24)
              .map(parseOfferingReference)
              .filter((reference): reference is OfferingReference => Boolean(reference))
          )
        }
        notFoundContent={
          offeringsQuery.isLoading
            ? "Loading offerings…"
            : offeringsQuery.isError
            ? "Offerings could not be loaded"
            : "No offerings found"
        }
      />
    </FieldGroup>
  );
}

function OfferingEditor({
  block,
  onChange,
}: {
  block: OfferingCollectionBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  const selectedOfferings =
    block.source.mode === "selected" ? block.source.offerings : [];
  const layout = block.layout ?? "grid";
  const mobileColumns = block.mobileColumns ?? 1;

  const defaultButtonLabels: Record<OfferingCollectionBlock["collection"], string> = {
    all: "View offering",
    services: "Book now",
    store: "Buy now",
    liveEvents: "Reserve a spot",
  };

  if (block.collection !== "all") {
    const isSessionBlock = block.collection === "services";
    const isLiveEventBlock = block.collection === "liveEvents";
    return (
      <>
        <FieldGroup>
          <label>Layout</label>
          <div className="offering-layout-options">
            {(["grid", "list"] as const).map((option) => (
              <button
                type="button"
                className={`offering-layout-option ${
                  layout === option ? "selected" : ""
                }`}
                aria-pressed={layout === option}
                key={option}
                onClick={() => onChange({...block, layout: option})}
              >
                <span className="layout-option-label">
                  <i aria-hidden="true" />
                  {option === "grid" ? "Grid" : "List"}
                </span>
                <span className={`layout-option-graphic ${option}`} aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
              </button>
            ))}
          </div>
        </FieldGroup>
        <FieldGroup>
          <label>
            Block title <span className="optional-label">(optional)</span>
          </label>
          <Input
            maxLength={80}
            value={block.title}
            placeholder={
              isSessionBlock
                ? "Private sessions with me"
                : isLiveEventBlock
                ? "Upcoming events"
                : "My shop"
            }
            onChange={(event) => onChange({...block, title: event.target.value})}
          />
        </FieldGroup>
        <FieldGroup>
          <label>
            Block description <span className="optional-label">(optional)</span>
          </label>
          <Input.TextArea
            maxLength={100}
            rows={3}
            showCount
            value={block.description ?? ""}
            placeholder={
              isSessionBlock
                ? "Available offers and services"
                : isLiveEventBlock
                ? "Join my live events and workshops"
                : "Browse my products and services"
            }
            onChange={(event) =>
              onChange({...block, description: event.target.value || null})
            }
            onBlur={(event) =>
              onChange({...block, description: optionalString(event.target.value)})
            }
          />
        </FieldGroup>
        <SelectedOfferingPicker
          category={
            isSessionBlock ? "services" : isLiveEventBlock ? "liveEvents" : undefined
          }
          categories={
            isSessionBlock || isLiveEventBlock
              ? undefined
              : ["digitalProducts", "productServices"]
          }
          label={
            isSessionBlock
              ? "Select sessions to display on your profile"
              : isLiveEventBlock
              ? "Select live events to display on your profile"
              : "Select products to display on your profile"
          }
          offerings={selectedOfferings}
          onChange={(offerings) =>
            onChange({...block, source: {mode: "selected", offerings}})
          }
        />
        <Button
          block
          className="create-offering-action"
          href={
            isSessionBlock
              ? "/calls/new"
              : isLiveEventBlock
              ? "/live-classes/new"
              : "/products/new"
          }
        >
          <PlusOutlined />
          {isSessionBlock
            ? "Create a new session"
            : isLiveEventBlock
            ? "Create a new live event"
            : "Create a new product"}
        </Button>
      </>
    );
  }

  return (
    <>
      <FieldGroup>
        <label>Section title</label>
        <Input
          maxLength={80}
          value={block.title}
          placeholder="Optional"
          onChange={(event) => onChange({...block, title: event.target.value})}
        />
      </FieldGroup>
      <FieldGroup>
        <label>Section description</label>
        <Input.TextArea
          maxLength={100}
          rows={3}
          showCount
          value={block.description ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, description: event.target.value || null})
          }
          onBlur={(event) =>
            onChange({...block, description: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      <FieldGroup>
        <label>Layout</label>
        <Segmented
          block
          value={layout}
          options={[
            {value: "grid", label: "Grid"},
            {value: "list", label: "List"},
          ]}
          onChange={(value) => onChange({...block, layout: value as "grid" | "list"})}
        />
      </FieldGroup>
      {layout === "grid" ? (
        <FieldGroup>
          <label>Cards on mobile</label>
          <Segmented
            block
            value={mobileColumns}
            options={[
              {value: 1, label: "1 per row"},
              {value: 2, label: "2 per row"},
            ]}
            onChange={(value) => onChange({...block, mobileColumns: value as 1 | 2})}
          />
          <div className="field-hint">
            Two per row uses a compact card so visitors can scan more offerings without
            excessive scrolling.
          </div>
        </FieldGroup>
      ) : null}
      <SelectedOfferingPicker
        offerings={selectedOfferings}
        label="Select offerings to display on your profile"
        onChange={(offerings) =>
          onChange({...block, source: {mode: "selected", offerings}})
        }
      />
      <FieldGroup>
        <label>Button text</label>
        <Input
          maxLength={40}
          value={block.buttonLabel ?? ""}
          placeholder={defaultButtonLabels[block.collection]}
          onChange={(event) =>
            onChange({...block, buttonLabel: optionalString(event.target.value)})
          }
        />
        <div className="field-hint">
          Leave blank to use each offering's existing call to action.
        </div>
      </FieldGroup>
      <FieldGroup>
        <label>Maximum items</label>
        <InputNumber
          min={1}
          max={24}
          precision={0}
          value={block.limit}
          style={{width: "100%"}}
          onChange={(limit) => onChange({...block, limit: Number(limit || 1)})}
        />
      </FieldGroup>
    </>
  );
}

function CustomLinkEditor({
  block,
  onChange,
}: {
  block: CustomLinkBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  return (
    <>
      <FieldGroup>
        <label>Section title</label>
        <Input
          maxLength={80}
          value={block.title ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, title: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      {block.links.map((link, index) => (
        <RepeaterItem key={index}>
          <RepeaterHeader
            label={`Link ${index + 1}`}
            disabled={block.links.length === 1}
            onRemove={() => onChange({...block, links: removeAt(block.links, index)})}
          />
          <FieldGroup>
            <label>Label</label>
            <Input
              maxLength={80}
              value={link.label}
              onChange={(event) =>
                onChange({
                  ...block,
                  links: replaceAt(block.links, index, {
                    ...link,
                    label: event.target.value,
                  }),
                })
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>Description</label>
            <Input
              maxLength={160}
              value={link.description ?? ""}
              placeholder="Optional"
              onChange={(event) =>
                onChange({
                  ...block,
                  links: replaceAt(block.links, index, {
                    ...link,
                    description: optionalString(event.target.value),
                  }),
                })
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>URL</label>
            <Input
              type="url"
              value={link.url}
              placeholder="https://…"
              onChange={(event) =>
                onChange({
                  ...block,
                  links: replaceAt(block.links, index, {
                    ...link,
                    url: event.target.value,
                  }),
                })
              }
            />
          </FieldGroup>
        </RepeaterItem>
      ))}
      <Button
        block
        icon={<PlusOutlined />}
        disabled={block.links.length >= 12}
        onClick={() =>
          onChange({
            ...block,
            links: [
              ...block.links,
              {label: "New link", description: null, url: "https://coachli.co"},
            ],
          })
        }
      >
        Add link
      </Button>
    </>
  );
}

function RichTextEditor({
  block,
  onChange,
}: {
  block: RichTextBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  const topicIndex = block.content.findIndex((node) => node.type === "heading");
  const contentIndex = block.content.findIndex((node) => node.type !== "heading");
  const topic = topicIndex >= 0 ? block.content[topicIndex].text : "";
  const content = contentIndex >= 0 ? block.content[contentIndex].text : "";

  const updateTopic = (text: string) => {
    if (topicIndex >= 0) {
      const nextContent: RichTextBlock["content"] = text
        ? replaceAt(block.content, topicIndex, {type: "heading", text})
        : removeAt(block.content, topicIndex);
      onChange({...block, content: nextContent});
      return;
    }
    if (text) {
      onChange({...block, content: [{type: "heading", text}, ...block.content]});
    }
  };

  const updateContent = (text: string) => {
    if (contentIndex >= 0) {
      onChange({
        ...block,
        content: replaceAt(block.content, contentIndex, {type: "paragraph", text}),
      });
      return;
    }
    onChange({...block, content: [...block.content, {type: "paragraph", text}]});
  };

  return (
    <>
      <FieldGroup>
        <label>
          Block title <span className="optional-label">(optional)</span>
        </label>
        <Input
          maxLength={2000}
          value={topic}
          placeholder="e.g., My journey as a creator"
          onChange={(event) => updateTopic(event.target.value)}
        />
      </FieldGroup>
      <FieldGroup>
        <label>Block description</label>
        <div className="text-block-rich-editor">
          <ReactQuill
            theme="snow"
            modules={TEXT_BLOCK_EDITOR_MODULES}
            formats={TEXT_BLOCK_EDITOR_FORMATS}
            value={content}
            placeholder="Type your content here"
            onChange={(html, _delta, _source, editor) => {
              const plainText = editor.getText().replace(/\n$/, "");
              if (plainText.length <= 2000) {
                updateContent(plainText.trim() ? html : "");
              }
            }}
          />
        </div>
      </FieldGroup>
    </>
  );
}

function TestimonialsEditor({
  block,
  onChange,
}: {
  block: TestimonialsBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  return (
    <>
      <FieldGroup>
        <label>Section title</label>
        <Input
          maxLength={80}
          value={block.title ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, title: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      {block.testimonials.map((testimonial, index) => (
        <RepeaterItem key={index}>
          <RepeaterHeader
            label={`Testimonial ${index + 1}`}
            disabled={block.testimonials.length === 1}
            onRemove={() =>
              onChange({...block, testimonials: removeAt(block.testimonials, index)})
            }
          />
          <FieldGroup>
            <label>Quote</label>
            <Input.TextArea
              autoSize={{minRows: 3, maxRows: 7}}
              maxLength={600}
              value={testimonial.quote}
              onChange={(event) =>
                onChange({
                  ...block,
                  testimonials: replaceAt(block.testimonials, index, {
                    ...testimonial,
                    quote: event.target.value,
                  }),
                })
              }
            />
          </FieldGroup>
          <div className="inline-fields">
            <FieldGroup>
              <label>Author</label>
              <Input
                maxLength={100}
                value={testimonial.author}
                onChange={(event) =>
                  onChange({
                    ...block,
                    testimonials: replaceAt(block.testimonials, index, {
                      ...testimonial,
                      author: event.target.value,
                    }),
                  })
                }
              />
            </FieldGroup>
            <FieldGroup>
              <label>Role</label>
              <Input
                maxLength={120}
                value={testimonial.role ?? ""}
                placeholder="Optional"
                onChange={(event) =>
                  onChange({
                    ...block,
                    testimonials: replaceAt(block.testimonials, index, {
                      ...testimonial,
                      role: optionalString(event.target.value),
                    }),
                  })
                }
              />
            </FieldGroup>
          </div>
          <FieldGroup>
            <label>Avatar URL</label>
            <Input
              type="url"
              value={testimonial.avatarUrl ?? ""}
              placeholder="Optional https://…"
              onChange={(event) =>
                onChange({
                  ...block,
                  testimonials: replaceAt(block.testimonials, index, {
                    ...testimonial,
                    avatarUrl: optionalString(event.target.value),
                  }),
                })
              }
            />
          </FieldGroup>
        </RepeaterItem>
      ))}
      <Button
        block
        icon={<PlusOutlined />}
        disabled={block.testimonials.length >= 12}
        onClick={() =>
          onChange({
            ...block,
            testimonials: [
              ...block.testimonials,
              {
                quote: "Add a short quote from a client.",
                author: "Client name",
                role: null,
                avatarUrl: null,
              },
            ],
          })
        }
      >
        Add testimonial
      </Button>
    </>
  );
}

function FaqEditor({
  block,
  onChange,
}: {
  block: FaqBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  return (
    <>
      <FieldGroup>
        <label>Section title</label>
        <Input
          maxLength={80}
          value={block.title ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, title: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      {block.items.map((item, index) => (
        <RepeaterItem key={index}>
          <RepeaterHeader
            label={`Question ${index + 1}`}
            disabled={block.items.length === 1}
            onRemove={() => onChange({...block, items: removeAt(block.items, index)})}
          />
          <FieldGroup>
            <label>Question</label>
            <Input
              maxLength={180}
              value={item.question}
              onChange={(event) =>
                onChange({
                  ...block,
                  items: replaceAt(block.items, index, {
                    ...item,
                    question: event.target.value,
                  }),
                })
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>Answer</label>
            <Input.TextArea
              autoSize={{minRows: 3, maxRows: 8}}
              maxLength={1200}
              value={item.answer}
              onChange={(event) =>
                onChange({
                  ...block,
                  items: replaceAt(block.items, index, {
                    ...item,
                    answer: event.target.value,
                  }),
                })
              }
            />
          </FieldGroup>
        </RepeaterItem>
      ))}
      <Button
        block
        icon={<PlusOutlined />}
        disabled={block.items.length >= 20}
        onClick={() =>
          onChange({
            ...block,
            items: [
              ...block.items,
              {question: "New question", answer: "Add the answer."},
            ],
          })
        }
      >
        Add question
      </Button>
    </>
  );
}

function EmailSignupEditor({
  block,
  onChange,
}: {
  block: EmailSignupBlock;
  onChange: (block: PublicProfileBlock) => void;
}) {
  return (
    <>
      <FieldGroup>
        <label>Title</label>
        <Input
          maxLength={100}
          value={block.title}
          onChange={(event) => onChange({...block, title: event.target.value})}
        />
      </FieldGroup>
      <FieldGroup>
        <label>Description</label>
        <Input.TextArea
          maxLength={240}
          autoSize={{minRows: 2, maxRows: 5}}
          value={block.description ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, description: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      <FieldGroup>
        <label>Button label</label>
        <Input
          maxLength={40}
          value={block.buttonLabel}
          onChange={(event) => onChange({...block, buttonLabel: event.target.value})}
        />
      </FieldGroup>
      <FieldGroup>
        <label>Form action URL</label>
        <Input
          type="url"
          value={block.formAction ?? ""}
          placeholder="Optional https://…"
          onChange={(event) =>
            onChange({...block, formAction: optionalString(event.target.value)})
          }
        />
        <div className="field-hint">
          Use the form endpoint supplied by your email provider. Without one, the preview
          button remains disabled.
        </div>
      </FieldGroup>
      <FieldGroup>
        <label>Consent note</label>
        <Input.TextArea
          maxLength={240}
          autoSize={{minRows: 2, maxRows: 4}}
          value={block.consentLabel ?? ""}
          placeholder="Optional privacy or consent text"
          onChange={(event) =>
            onChange({...block, consentLabel: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
    </>
  );
}

function ImageEditor({
  block,
  onChange,
}: {
  block: Extract<PublicProfileBlock, {type: "image"}>;
  onChange: (block: PublicProfileBlock) => void;
}) {
  const uploadImage = useUploadPublicProfileImage();

  return (
    <>
      <FieldGroup>
        <label>Image</label>
        <div className="inline-action-field">
          <Input
            type="url"
            value={block.src}
            placeholder="https://…"
            onChange={(event) => onChange({...block, src: event.target.value})}
          />
          <Upload
            accept="image/jpeg,image/png,image/gif,image/webp"
            showUploadList={false}
            beforeUpload={(file) => {
              uploadImage.mutate(file, {
                onSuccess: ({url}) => {
                  const suggestedAlt = file.name.replace(/\.[^.]+$/, "").trim();
                  onChange({
                    ...block,
                    src: url,
                    alt: block.alt || suggestedAlt,
                  });
                },
              });
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploadImage.isLoading}>
              Upload
            </Button>
          </Upload>
        </div>
        <div className="field-hint">
          Upload a JPEG, PNG, GIF or WebP up to 5 MB, or paste a direct image URL.
        </div>
      </FieldGroup>
      <FieldGroup>
        <label>Alternative text</label>
        <Input
          maxLength={160}
          value={block.alt}
          placeholder="Describe the image for accessibility"
          onChange={(event) => onChange({...block, alt: event.target.value})}
        />
      </FieldGroup>
      <FieldGroup>
        <label>Caption</label>
        <Input
          maxLength={240}
          value={block.caption ?? ""}
          placeholder="Optional"
          onChange={(event) =>
            onChange({...block, caption: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
      <FieldGroup>
        <label>Destination URL</label>
        <Input
          type="url"
          value={block.linkUrl ?? ""}
          placeholder="Optional https://…"
          onChange={(event) =>
            onChange({...block, linkUrl: optionalString(event.target.value)})
          }
        />
      </FieldGroup>
    </>
  );
}

function BlockFields({
  block,
  onChange,
}: {
  block: PublicProfileBlock;
  onChange: (block: PublicProfileBlock) => void;
}): JSX.Element {
  switch (block.type) {
    case "offeringCollection":
      return <OfferingEditor block={block} onChange={onChange} />;
    case "customLink":
      return <CustomLinkEditor block={block} onChange={onChange} />;
    case "socialLinks":
      return (
        <>
          <FieldGroup>
            <label>Section title</label>
            <Input
              maxLength={80}
              value={block.title ?? ""}
              placeholder="Optional"
              onChange={(event) =>
                onChange({...block, title: optionalString(event.target.value)})
              }
            />
          </FieldGroup>
          <Alert
            type="info"
            showIcon
            message="This block uses the social links saved in your Profile settings."
          />
        </>
      );
    case "richText":
      return <RichTextEditor block={block} onChange={onChange} />;
    case "image":
      return <ImageEditor block={block} onChange={onChange} />;
    case "divider":
      return (
        <FieldGroup>
          <label>Line style</label>
          <Select
            value={block.style}
            style={{width: "100%"}}
            options={[
              {value: "solid", label: "Solid"},
              {value: "dashed", label: "Dashed"},
              {value: "dotted", label: "Dotted"},
            ]}
            onChange={(style) => onChange({...block, style})}
          />
        </FieldGroup>
      );
    case "video":
      return (
        <>
          <FieldGroup>
            <label>Title</label>
            <Input
              maxLength={80}
              value={block.title ?? ""}
              placeholder="Optional"
              onChange={(event) =>
                onChange({...block, title: optionalString(event.target.value)})
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>Video embed URL</label>
            <Input
              type="url"
              value={block.embedUrl}
              placeholder="https://www.youtube.com/embed/…"
              onChange={(event) => onChange({...block, embedUrl: event.target.value})}
            />
            <div className="field-hint">
              Use an embed URL, not the normal watch-page URL.
            </div>
          </FieldGroup>
        </>
      );
    case "testimonials":
      return <TestimonialsEditor block={block} onChange={onChange} />;
    case "countdown": {
      const date = new Date(block.targetAt);
      const localDate = Number.isNaN(date.getTime())
        ? ""
        : new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
            .toISOString()
            .slice(0, 16);
      return (
        <>
          <FieldGroup>
            <label>Title</label>
            <Input
              maxLength={100}
              value={block.title}
              onChange={(event) => onChange({...block, title: event.target.value})}
            />
          </FieldGroup>
          <FieldGroup>
            <label>Target date and time</label>
            <Input
              type="datetime-local"
              value={localDate}
              onChange={(event) =>
                onChange({
                  ...block,
                  targetAt: event.target.value
                    ? new Date(event.target.value).toISOString()
                    : "",
                })
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>Message after countdown</label>
            <Input
              maxLength={80}
              value={block.expiredLabel}
              onChange={(event) => onChange({...block, expiredLabel: event.target.value})}
            />
          </FieldGroup>
        </>
      );
    }
    case "embed":
      return (
        <>
          <FieldGroup>
            <label>Title</label>
            <Input
              maxLength={80}
              value={block.title ?? ""}
              placeholder="Optional"
              onChange={(event) =>
                onChange({...block, title: optionalString(event.target.value)})
              }
            />
          </FieldGroup>
          <FieldGroup>
            <label>Embed URL</label>
            <Input
              type="url"
              value={block.url}
              placeholder="https://…"
              onChange={(event) => onChange({...block, url: event.target.value})}
            />
          </FieldGroup>
          <FieldGroup>
            <label>Height in pixels</label>
            <InputNumber
              min={180}
              max={1200}
              precision={0}
              value={block.height}
              style={{width: "100%"}}
              onChange={(height) => onChange({...block, height: Number(height || 180)})}
            />
          </FieldGroup>
        </>
      );
    case "emailSignup":
      return <EmailSignupEditor block={block} onChange={onChange} />;
    case "faq":
      return <FaqEditor block={block} onChange={onChange} />;
  }
}

export function PublicProfileBlockEditor({
  block,
  index,
  total,
  expanded,
  issues,
  onToggle,
  onChange,
  onMove,
  onDelete,
}: {
  block: PublicProfileBlock;
  index: number;
  total: number;
  expanded: boolean;
  issues: string[];
  onToggle: () => void;
  onChange: (block: PublicProfileBlock) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
}) {
  const blockName = getPublicProfileBlockName(block);
  const editorId = `${block.id}-editor`;
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      as="div"
      value={block.id}
      dragListener={false}
      dragControls={dragControls}
      className="profile-block-reorder-item"
      whileDrag={{scale: 1.01, zIndex: 2}}
    >
      <BlockCard $expanded={expanded}>
        <div className="block-header">
          <Button
            type="text"
            size="small"
            className="block-drag-handle"
            aria-label={`Drag ${blockName} to reorder. Use Arrow Up or Arrow Down for keyboard reordering.`}
            aria-keyshortcuts="ArrowUp ArrowDown"
            icon={<HolderOutlined />}
            onPointerDown={(event) => dragControls.start(event)}
            onKeyDown={(event) => {
              if (event.key === "ArrowUp" && index > 0) {
                event.preventDefault();
                onMove(-1);
              }
              if (event.key === "ArrowDown" && index < total - 1) {
                event.preventDefault();
                onMove(1);
              }
            }}
          />
          <button
            type="button"
            className="block-toggle"
            aria-expanded={expanded}
            aria-controls={editorId}
            onClick={onToggle}
          >
            <span className="identity">
              <strong>{blockName}</strong>
            </span>
            {issues.length ? (
              <Tag color="red">
                {issues.length} issue{issues.length === 1 ? "" : "s"}
              </Tag>
            ) : null}
          </button>
          <span className="block-actions">
            <Switch
              size="small"
              checked={block.visible}
              aria-label={`${block.visible ? "Hide" : "Show"} ${blockName}`}
              onChange={(visible) => onChange({...block, visible})}
            />
            <Popconfirm
              title="Delete this block?"
              okText="Delete"
              okButtonProps={{danger: true}}
              onConfirm={onDelete}
            >
              <Button
                type="text"
                size="small"
                className="block-delete-action"
                icon={<img src="/icons/delete-icon.png" alt="" aria-hidden="true" />}
                aria-label={`Delete ${blockName}`}
              />
            </Popconfirm>
            <Button
              type="text"
              size="small"
              className="block-chevron-action"
              aria-label={`${expanded ? "Collapse" : "Expand"} ${blockName}`}
              icon={
                <span
                  className={`content-accordion-chevron ${expanded ? "open" : ""}`}
                  aria-hidden="true"
                >
                  ⌄
                </span>
              }
              onClick={onToggle}
            />
          </span>
        </div>
        {expanded ? (
          <div className="block-body" id={editorId}>
            {issues.length ? (
              <Alert
                type="error"
                showIcon
                message="Complete this block before saving"
                description={issues.slice(0, 3).join(" ")}
              />
            ) : null}
            <BlockFields block={block} onChange={onChange} />
          </div>
        ) : null}
      </BlockCard>
    </Reorder.Item>
  );
}
